import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip,
    CircularProgress, Alert, IconButton, TextField, InputAdornment,
    FormControl, InputLabel, Select, MenuItem, TablePagination
} from '@mui/material';
import { useNotify } from 'react-admin';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ClearIcon from '@mui/icons-material/Clear';
import GroupsIcon from '@mui/icons-material/Groups';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

function GruppiPage() {
    const navigate = useNavigate();
    const notify = useNotify();
    const [gruppi, setGruppi] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [filterStatus, setFilterStatus] = React.useState('all');
    const [page, setPage] = React.useState(0);
    const [limit, setLimit] = React.useState(25);
    const [total, setTotal] = React.useState(0);

    const buildQuery = () => {
        const query = { $limit: limit, $skip: page * limit, $sort: { ordinamento: 1, nome: 1 } };
        const filters = {};

        if (searchTerm.trim()) {
            const search = `%${searchTerm.trim()}%`;
            filters.$or = [
                { nome: { $like: search } },
                { note: { $like: search } }
            ];
        }

        if (filterStatus !== 'all') {
            switch (filterStatus) {
                case 'active': filters.flAttivo = 1; filters.flAnnullato = 0; break;
                case 'inactive': filters.flAttivo = 0; break;
                case 'hidden': filters.flVisibile = 0; break;
                case 'cancelled': filters.flAnnullato = 1; break;
            }
        }

        if (Object.keys(filters).length > 0) Object.assign(query, filters);
        return query;
    };

    const loadGruppi = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();
            const query = buildQuery();
            const response = await axios.get(`${host}/tbl-gruppo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: query
            });
            setGruppi(response.data.data || []);
            setTotal(response.data.total || 0);
        } catch (error) {
            console.error("Errore caricamento gruppi:", error);
            setError("Errore nel caricamento dei gruppi");
            notify("Errore nel caricamento dei gruppi", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleVisibility = async (gruppo) => {
        try {
            const jwt = getJwt();
            await axios.patch(`${host}/tbl-gruppo/${gruppo.id}`,
                { flVisibile: gruppo.flVisibile ? 0 : 1 },
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );
            notify(`Gruppo ${gruppo.flVisibile ? 'nascosto' : 'mostrato'}`, { type: 'success' });
            loadGruppi();
        } catch (error) {
            notify("Errore nell'aggiornamento", { type: 'error' });
        }
    };

    const resetFilters = () => {
        setSearchTerm('');
        setFilterStatus('all');
        setPage(0);
    };

    React.useEffect(() => {
        loadGruppi();
    }, [page, limit, searchTerm, filterStatus]);

    const stats = {
        totali: total,
        attivi: gruppi.filter(g => g.flAttivo && !g.flAnnullato).length,
        inattivi: gruppi.filter(g => !g.flAttivo).length
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        <GroupsIcon sx={{ fontSize: 32, verticalAlign: 'middle', mr: 1 }} />
                        Gestione Gruppi
                    </Typography>
                    <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddCircleIcon />}
                            sx={{
                                color: '#4A90E2',
                                borderColor: '#4A90E2',
                                fontWeight: 600,
                                borderRadius: 2,
                                py: 1.5,
                                '&:hover': { borderColor: '#357ABD', bgcolor: 'rgba(74, 144, 226, 0.04)' }
                            }}
                            onClick={() => navigate('/gruppi/create')}>
                            Nuovo Gruppo
                        </Button>
                    </Card>
                </Box>

                <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Cerca gruppo..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Stato</InputLabel>
                                <Select value={filterStatus} label="Stato" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
                                    <MenuItem value="all">Tutti</MenuItem>
                                    <MenuItem value="active">Attivi</MenuItem>
                                    <MenuItem value="inactive">Inattivi</MenuItem>
                                    <MenuItem value="hidden">Nascosti</MenuItem>
                                    <MenuItem value="cancelled">Annullati</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={5}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button size="small" variant="outlined" onClick={resetFilters} disabled={searchTerm === '' && filterStatus === 'all'}>
                                    Cancella filtri
                                </Button>
                                <Button size="small" variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={loadGruppi} disabled={loading}>
                                    Aggiorna
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4A90E2' }}>{stats.totali}</Typography>
                            <Typography variant="body2" color="text.secondary">Gruppi Totali</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>{stats.attivi}</Typography>
                            <Typography variant="body2" color="text.secondary">Attivi</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>{stats.inattivi}</Typography>
                            <Typography variant="body2" color="text.secondary">Inattivi</Typography>
                        </Card>
                    </Grid>
                </Grid>

                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                    {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                    {loading && !gruppi.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : gruppi.length > 0 ? (
                        <>
                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Ordinamento</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Stato</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600 }}>Azioni</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {gruppi.map((gruppo, index) => {
                                            let status = 'Attivo', color = '#4caf50', bgcolor = '#e8f5e8';
                                            if (gruppo.flAnnullato) { status = 'Annullato'; color = '#f44336'; bgcolor = '#ffebee'; }
                                            else if (!gruppo.flAttivo) { status = 'Inattivo'; color = '#ff9800'; bgcolor = '#fff3e0'; }
                                            else if (!gruppo.flVisibile) { status = 'Nascosto'; color = '#9e9e9e'; bgcolor = '#f5f5f5'; }

                                            return (
                                                <TableRow key={gruppo.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }, bgcolor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit' }}>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <GroupsIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{gruppo.nome || 'N/A'}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {gruppo.note ? (gruppo.note.length > 60 ? `${gruppo.note.substring(0, 60)}...` : gruppo.note) : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={gruppo.ordinamento || 0} size="small" sx={{ bgcolor: '#f5f5f5', fontWeight: 500 }} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={status} size="small" sx={{ color, bgcolor, fontWeight: 500 }} />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                            <IconButton size="small" onClick={() => navigate(`/gruppi/${gruppo.id}`)}>
                                                                <InfoIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => navigate(`/gruppi/${gruppo.id}/edit`)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleToggleVisibility(gruppo)} color={gruppo.flVisibile ? "default" : "warning"}>
                                                                {gruppo.flVisibile ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                rowsPerPage={limit}
                                onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage="Righe per pagina:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                            />
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 6 }}>
                            <Typography variant="h6" color="text.secondary">Nessun gruppo trovato</Typography>
                        </Box>
                    )}
                </Card>
            </motion.div>
        </Box>
    );
}

export default GruppiPage;
